<script setup>
// ============================================================================
// BonBoZ: KHỐI VÒNG NỬA — khuôn dùng chung cho hai thẻ đầu trang Tương tác
// ("Tương Tác khách hàng" và "Đơn hàng"). Hai thẻ giống nhau tới từng lề nên
// tách ra một khuôn: sửa một chỗ thì cả hai đổi theo, không có chuyện thẻ này
// lề 16 thẻ kia lề 20.
//
// Cấu trúc, đúng như Pancake: cung nửa vòng ở giữa, con số tổng và huy hiệu
// chênh lệch đè lên giữa cung, hai mốc phần trăm ở hai chân cung, chú giải bên
// phải, và một hàng hai ô có icon ở dưới cùng.
// ============================================================================
import { computed } from 'vue';
import BieuDoNuaTron from './BieuDoNuaTron.vue';
import { mauCss } from 'dashboard/helper/mauBieuDo';

const props = defineProps({
  tieuDe: { type: String, required: true },
  moTa: { type: String, default: '' },
  nhanGiua: { type: String, required: true },
  tong: { type: [Number, String], required: true },
  lech: { type: Number, default: null },
  // [{ khoa, nhan, so, bien }] — đúng hai phần tử: mới rồi cũ.
  phan: { type: Array, default: () => [] },
  // [{ khoa, nhan, giaTri, lech, icon, bien }] — hàng hai ô dưới cùng.
  oDuoi: { type: Array, default: () => [] },
  dangTai: { type: Boolean, default: false },
});

const tongPhan = computed(() => props.phan.reduce((s, p) => s + p.so, 0));

// Phần trăm ở hai chân cung. Không có gì thì hiện 0% chứ không để trống: hai
// mốc trống dưới một cung xám nhìn như hỏng.
const phanTram = computed(() =>
  props.phan.map(p =>
    tongPhan.value ? Math.round((p.so / tongPhan.value) * 10000) / 100 : 0
  )
);

const mieng = computed(() =>
  props.phan.map(p => ({ nhan: p.nhan, so: p.so, nen: mauCss(p.bien) }))
);

// Mũi tên là HÌNH chứ không phải màu, nên người không phân biệt được màu vẫn
// đọc được. `null` = không có kỳ trước để so, lúc đó im lặng thay vì hiện 0%.
const nhip = n => {
  if (n === null || n === undefined) return null;
  if (n > 0) return { mui: '▲', huong: 'text-n-teal-11 bg-n-teal-3' };
  if (n < 0) return { mui: '▼', huong: 'text-n-ruby-11 bg-n-ruby-3' };
  return { mui: '', huong: 'text-n-slate-11 bg-n-alpha-1' };
};
</script>

<template>
  <section
    class="flex flex-col p-4 border rounded-xl border-n-weak bg-n-solid-2"
  >
    <div class="flex items-start justify-between gap-3 mb-2">
      <div class="min-w-0">
        <h3 class="m-0 text-sm font-semibold truncate text-n-slate-12">
          {{ tieuDe }}
        </h3>
        <p v-if="moTa" class="m-0 mt-0.5 text-xs truncate text-n-slate-11">
          {{ moTa }}
        </p>
      </div>
      <slot name="goc-phai" />
    </div>

    <div v-if="dangTai" class="h-48 rounded-lg bg-n-alpha-2 animate-pulse" />

    <template v-else>
      <div class="flex flex-wrap items-center justify-center gap-6">
        <div class="relative w-56 shrink-0">
          <!-- Khung cao đúng NỬA chiều rộng cộng lề: cung chỉ chiếm nửa trên,
               chừa khung cao gấp đôi là để lại một khoảng trống lớn dưới nó. -->
          <div class="relative h-32">
            <div class="absolute inset-0">
              <BieuDoNuaTron
                :mieng="mieng"
                :trong="mauCss('--bonboz-mo', 0.1)"
              />
            </div>
            <!-- Chữ giữa là HTML ĐÈ lên canvas — dịch và style dễ hơn plugin vẽ
                 chữ của chart.js nhiều. -->
            <div
              class="absolute inset-x-0 bottom-0 text-center pointer-events-none"
            >
              <span class="block text-xs text-n-slate-11">{{ nhanGiua }}</span>
              <span
                class="block text-3xl font-semibold tabular-nums text-n-slate-12"
              >
                {{ tong }}
              </span>
            </div>
          </div>

          <div class="flex items-start justify-between mt-1">
            <span class="text-xs tabular-nums text-n-slate-11">
              {{ phanTram[0] }}%
            </span>
            <span
              v-if="nhip(lech)"
              class="px-1.5 py-0.5 text-xs rounded-full tabular-nums"
              :class="nhip(lech).huong"
            >
              <span aria-hidden="true">{{ nhip(lech).mui }}</span>
              {{ Math.abs(lech) }}%
            </span>
            <span class="text-xs tabular-nums text-n-slate-11">
              {{ phanTram[1] }}%
            </span>
          </div>
        </div>

        <ul class="flex flex-col gap-3 p-0 m-0 list-none min-w-40">
          <li
            v-for="p in phan"
            :key="p.khoa"
            class="flex items-center gap-2 text-sm"
          >
            <span
              class="rounded size-4 shrink-0"
              :style="{ background: `rgb(var(${p.bien}))` }"
              aria-hidden="true"
            />
            <span class="flex-1 truncate text-n-slate-11">{{ p.nhan }}</span>
            <span class="tabular-nums text-n-slate-12">{{ p.so }}</span>
          </li>
        </ul>
      </div>

      <!-- Hàng hai ô dưới cùng, ngăn với phần trên bằng một đường kẻ. -->
      <div
        class="grid grid-cols-1 gap-4 pt-4 mt-4 border-t sm:grid-cols-2 border-n-weak"
      >
        <div
          v-for="o in oDuoi"
          :key="o.khoa"
          v-tooltip.top="o.gt || null"
          class="flex items-center gap-3 min-w-0"
        >
          <span
            class="grid rounded-lg size-11 shrink-0 place-items-center"
            :style="{ background: `rgb(var(${o.bien}) / 0.12)` }"
          >
            <span
              class="block size-5"
              :class="o.icon"
              :style="{ color: `rgb(var(${o.bien}))` }"
              aria-hidden="true"
            />
          </span>
          <span class="min-w-0">
            <span class="block text-sm truncate text-n-slate-11">
              {{ o.nhan }}
            </span>
            <span class="flex items-baseline gap-1.5">
              <span class="text-xl font-semibold tabular-nums text-n-slate-12">
                {{ o.giaTri }}
              </span>
              <span
                v-if="nhip(o.lech)"
                class="text-xs whitespace-nowrap"
                :class="nhip(o.lech).huong.split(' ')[0]"
              >
                <span aria-hidden="true">{{ nhip(o.lech).mui }}</span>
                {{ Math.abs(o.lech) }}%
              </span>
            </span>
          </span>
        </div>
      </div>
    </template>
  </section>
</template>
